import React, { useState, useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { Grid } from "@mui/material";
import {
  BackendConfigurations,
  BackendCountryConfigurations,
  configurationsProduction,
  configurationsStaging,
} from "app/configuration";
import useStores from "app/hooks/useStores";
import { COUNTRY_CODE_KEY, STORE_ID_KEY } from "app/utils/constants/storage";
const { REACT_APP_CONFIG_LIVE } = process.env;

const configuration: BackendConfigurations =
  REACT_APP_CONFIG_LIVE === "true"
    ? configurationsProduction
    : configurationsStaging;

const CountrySelect = () => {
  const { stores } = useStores();
  const [selectedCountry, setSelectedCountry] = useState<"KW" | "SA">(
    configuration.kw.countryCode
  );
  const [activeStoreId, setActiveStoreId] = useState<number>(null);

  type CountryKey = keyof BackendConfigurations; // "kw" | "sa"
  const countryKey = selectedCountry.toLowerCase() as CountryKey;
  const activeCountryConfig = configuration[countryKey];
  const activeCountryStores = stores
    ? stores.filter(
        (store) => store.countryId === activeCountryConfig.CountryId
      )
    : [];

  useEffect(() => {
    const countryLocalerString = localStorage.getItem(COUNTRY_CODE_KEY);
    const storeIdString = localStorage.getItem(STORE_ID_KEY);
    const authUser = localStorage.getItem("authUser");

    if (storeIdString) {
      setActiveStoreId(Number(storeIdString));
    }

    // Set the selected country from local storage if available
    if (countryLocalerString) {
      setSelectedCountry(countryLocalerString as "KW" | "SA");
    }

    if (!countryLocalerString && !storeIdString && authUser) {
      // Both are not set , this could mean a fresh login, so we have to set the selectedCountry & activeStoreId
      if (stores?.length > 0) {
        // Set the selected country to the first store's country code
        const firstStore = stores[0];
        const countryId = firstStore.countryId;
        const activeCountryConfig = Object.values(configuration).find(
          (config) => config.CountryId === countryId
        );

        setSelectedCountry(activeCountryConfig?.countryCode);
        localStorage.setItem(
          COUNTRY_CODE_KEY,
          activeCountryConfig?.countryCode
        );

        // Set the active store ID to the first store of the selected country
        setActiveStoreId(firstStore.id);
        localStorage.setItem(STORE_ID_KEY, String(firstStore.id));
      }
    }
  }, []);

  const onChangeCountry = (event: SelectChangeEvent) => {
    const value = event.target.value;
    // Store the selected country in local storage
    localStorage.setItem("countryCode", value);

    // Update the selected country state
    setSelectedCountry(value as "KW" | "SA");

    const newConfig: BackendCountryConfigurations =
      configuration[value.toLowerCase() as CountryKey];

    // Change the active store ID to the default store ID of the selected country
    let storeId = stores.find(
      (store) =>
        store.countryId === newConfig.CountryId &&
        store.brandId === newConfig.defaultBrandId
    )?.id;

    if (!storeId) {
      // Fallback to the first store with same countryId if no brandId match
      storeId = stores.find(
        (store) => store.countryId === newConfig.CountryId
      )?.id;
    }

    setActiveStoreId(storeId ? storeId : 0);
    localStorage.setItem(STORE_ID_KEY, String(storeId));

    reloadWindow();
  };

  const onChangeStore = (event: SelectChangeEvent) => {
    const value = event.target.value;
    localStorage.setItem(STORE_ID_KEY, value);
    setActiveStoreId(Number(value));

    reloadWindow();
  };

  const reloadWindow = () => {
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <Grid
      container
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
      }}
      spacing={1}
    >
      <Grid item>
        <FormControl size="small">
          <Select
            labelId="country-label"
            id="country-select"
            value={selectedCountry}
            onChange={onChangeCountry}
          >
            {countries.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item>
        <FormControl size="small">
          {activeCountryStores.length > 0 && (
            <Select
              labelId="store-label"
              id="store-select"
              value={`${activeStoreId}`}
              onChange={onChangeStore}
            >
              {activeCountryStores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default React.memo(CountrySelect);

interface CountryType {
  code: string;
  label: string;
  phone?: string;
  suggested?: boolean;
}

const countries: readonly CountryType[] = [
  { code: "KW", label: "KW" },
  { code: "SA", label: "SA" },
];
